import type { VariantProps } from 'class-variance-authority';
import type { CategoryListItemDto } from '@/queries/categories';
import { cva } from 'class-variance-authority';
import Image from 'next/image';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';
import { isProduction } from '@/libs/getEnvironment';
import { cn } from '@/utils/cn';
import './styles.css';

const categoryCardVariants = cva(
  'relative z-10 block w-full h-20 overflow-hidden rounded-2xl category-card',
  {
    variants: {
      variant: {
        default: 'min-w-38.75 xss:w-full xss:max-w-44.5 md:max-w-[10.625rem] lg:max-w-[11.125rem]',
        lobby: 'sm:w-44.5',
        slider: 'w-38.75',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const instruction = {
  default: {
    mobile: { className: 'xss:hidden block', show: true },
    desktop: { className: 'hidden xss:block', show: true },
  },
  lobby: {
    mobile: { show: false },
    desktop: { show: true },
  },
  slider: {
    mobile: { show: true },
    desktop: { show: false },
  },
} as const;

type CategoryCardVariant = keyof typeof instruction;

const categoryCardCoverFrameVariants = cva('relative shrink-0 h-20', {
  variants: {
    variant: {
      default: 'w-[3.375rem] xss:w-17.5 hidden xss:block',
      lobby: 'w-17.5',
      slider: 'w-[3.375rem]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const categoryCardCoverImageVariants = cva('', {
  variants: {
    variant: {
      default: 'object-cover object-right',
      lobby: 'object-fill',
      slider: 'object-cover object-right',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const categoryCardCoverSizes: Record<CategoryCardVariant, string> = {
  default: '(max-width: 479px) 3.375rem, 4.375rem',
  lobby: '4.375rem',
  slider: '3.375rem',
};

const categoryCardNameVariants = cva('text-white text-sm', {
  variants: {
    variant: {
      default: 'pr-[.6188rem]',
      lobby: 'md:text-base pr-4',
      slider: 'pr-[.6188rem]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface CategoryCardProps extends Omit<VariantProps<typeof categoryCardVariants>, 'variant'> {
  category: CategoryListItemDto;
  className?: string;
  variant?: CategoryCardVariant;
}

export default function CategoryCard({ category, className, variant = 'default' }: CategoryCardProps) {
  // TODO: Add alias instead of id
  const href = isProduction() ? AppRoutes.categoryIdPaged(category.id) : AppRoutes.categoriesSlugPaged(category.alias);

  const desktopIcon = category.cover?.key ?? category.cover?.path;
  const mobileIcon = category.mobileListCover?.key ?? category.mobileListCover?.path ?? desktopIcon;
  const iconAlt = category.cover?.meta?.alt || category.name;

  const mobileClassName = instruction[variant].mobile && 'className' in instruction[variant].mobile ? instruction[variant].mobile.className : '';
  const desktopClassName = instruction[variant].desktop && 'className' in instruction[variant].desktop ? instruction[variant].desktop.className : '';

  return (
    <Link
      href={href}
      prefetch={false}
      className={cn(categoryCardVariants({ variant }), className)}
    >
      <div className="pointer-events-none absolute inset-0 z-0">
        {/* Block for optimized gradient */}
        <div className="category-card__glow" />

        {/* Gradient layers / glows */}
        <div className="category-card__primary-glow" />
        <div className="category-card__top-glow" />
        <div className="category-card__side-glow" />
        <div className="category-card__bottom-glow" />
      </div>

      <div className={cn('flex items-center gap-3 relative z-10 h-full', variant === 'lobby' && 'gap-2.5')}>
        { mobileIcon && instruction[variant].mobile.show && (
          <div className={cn(categoryCardCoverFrameVariants({ variant }), mobileClassName)}>
            <Image
              fill
              sizes={categoryCardCoverSizes[variant]}
              src={mobileIcon}
              alt={iconAlt}
              className={categoryCardCoverImageVariants({ variant })}
            />
          </div>
        )}

        {desktopIcon && instruction[variant].desktop.show && (
          <div className={cn(categoryCardCoverFrameVariants({ variant }), desktopClassName)}>
            <Image
              fill
              sizes={categoryCardCoverSizes[variant]}
              alt={iconAlt}
              src={desktopIcon}
              className={categoryCardCoverImageVariants({ variant })}
            />
          </div>
        )}

        {!desktopIcon && (
          <div className={cn(categoryCardCoverFrameVariants({ variant }), desktopClassName)} />
        )}

        <div className={cn(categoryCardNameVariants({ variant }), 'whitespace-normal')}>{category.name}</div>
      </div>
    </Link>
  );
}
